import axios from 'axios';
import { compressPdf, needsCompression } from './utils/pdfCompressor';

// Configuración base de Axios
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // Cambia esto a la URL de tu backend
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add this utility function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Update the api instance to include auth token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Updated to support date filtering
export const getShipments = async (startDate, endDate) => {
  try {
    let url = '/api/v1/shipments';
    //let url = '/api/v1/shipments?debug=true'; // Agregamos debug=true para filtrar solo pruebas
    if (startDate && endDate) {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipments:', error);
    throw error;
  }
};

/**
 * Obtiene los embarques para el dashboard de cumplimiento
 */
export const getComplianceShipments = async () => {
  try {
    // Eliminamos los parámetros de fecha para obtener todos los embarques
    const response = await api.get('/api/v1/compliance-dashboard');
    return response.data;
  } catch (error) {
    console.error("Error al obtener embarques para dashboard de cumplimiento:", error);
    throw error;
  }
};

/**
 * Obtiene los embarques para el dashboard de cumplimiento de recolecciones
 */
export const getPickupComplianceShipments = async () => {
  try {
    const response = await api.get('/api/v1/compliance-dashboard');
    return response.data;
  } catch (error) {
    console.error("Error al obtener embarques para dashboard de cumplimiento de recolecciones:", error);
    throw error;
  }
};

/**
 * Obtiene los embarques para el dashboard de cumplimiento del cliente (con token/interceptor)
 */
export const getClientShipments = async () => {
  try {
    const response = await api.get('/api/v1/compliance-dashboard/client');
    return response.data;
  } catch (error) {
    console.error('Error al obtener embarques para dashboard de cliente:', error);
    throw error;
  }
};

// Get shipment with its orders
export const getShipmentWithOrders = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/with-orders`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipment with orders:', error);
    throw error;
  }
};

// Get shipment with orders and products
export const getShipmentWithOrdersProducts = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/with-orders-products`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipment with orders and products:', error);
    throw error;
  }
};

// Get available orders for a shipment
export const getAvailableOrdersForShipment = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/available-orders`);
    return response.data;
  } catch (error) {
    console.error('Error fetching available orders:', error);
    throw error;
  }
};

// Get vendors/accounts
export const getVendors = async () => {
  try {
    const response = await api.get('/api/v1/cuentas');
    return response.data;
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw error;
  }
};

// Get transport lines
export const getTransportLines = async () => {
  try {
    const response = await api.get('/api/v1/lineas_transporte/');
    return response.data;
  } catch (error) {
    console.error('Error fetching transport lines:', error);
    throw error;
  }
};

// Get drivers by transport line
export const getDriversByLine = async (lineId) => {
  try {
    const response = await api.get(`/api/v1/lineas_transporte/${lineId}/conductores`);
    return response.data;
  } catch (error) {
    console.error('Error fetching drivers:', error);
    throw error;
  }
};

// Get comments for a shipment
export const getShipmentComments = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/comments`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipment comments:', error);
    throw error;
  }
};

// Add comment to a shipment
export const addShipmentComment = async (shipmentId, commentData) => {
  try {
    const response = await api.post(`/api/v1/shipments/${shipmentId}/comments`, commentData);
    return response.data;
  } catch (error) {
    console.error('Error adding shipment comment:', error);
    throw error;
  }
};

// Revert a shipment
export const revertShipment = async (shipmentId) => {
  try {
    // Get orders associated with the shipment
    const shipmentData = await getShipmentWithOrders(shipmentId);
    
    if (!shipmentData || !shipmentData.orders) {
      throw new Error('No orders found associated with this shipment');
    }
    
    // Revert each order with loading_order=1
    for (const order of shipmentData.orders) {
      await api.put(`/api/v1/orders/${order.id}/revert`, {
        shipment_id: null,
        Order_status: 'CREATED',
        loading_order: 1
      });
    }
    
    // Update shipment status and reset multiple-origin fields
    const response = await api.patch(`/api/v1/shipments/${shipmentId}`, {
      status: 'PENDIENTE',
      Substatus: 'CREADO',
      current_loading_phase: 0,
      total_loading_phases: 1,
      current_origin: null,
      loading_sequence: null,
    });
    
    // Also clear transport line and client info from cost record
    try {
      const costRecordResponse = await api.patch(`/api/v1/cost-records/clear-by-shipment/${shipmentId}`);
      console.log('Cost record cleared:', costRecordResponse.data);
    } catch (costError) {
      console.error('Error clearing cost record:', costError);
      // Continue execution even if cost record update fails
    }
    
    return response.data;
  } catch (error) {
    console.error('Error reverting shipment:', error);
    throw error;
  }
};

export const createShipment = async (shipmentData) => {
  try {
    const response = await api.post('/api/v1/shipments/', shipmentData); // Usando axios
    console.log('Embarque creado:', response.data); // Imprime la respuesta de la API
    return response.data; // Retorna la respuesta de la API
  } catch (error) {
    console.error('Error al crear el embarque:', error.response?.data || error.message);
    throw new Error('Error al crear el embarque');
  }
};

export const deleteShipment = async (shipmentId) => {
  try {
      const response = await api.delete(`/api/v1/shipments/${shipmentId}`);
      console.log("Embarque eliminado:", response.data);
      return response.data;
  } catch (error) {
      console.error("Error al eliminar el embarque:", error);
      throw error;  // Better to throw the error and handle it in the component
  }
};

export const getOrders = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await api.get('/api/v1/orders', { params });
  return response.data;
};

export const createOrder = async (orderData) => {
  console.log('Datos que recibe la API:', orderData);

  try {
    const response = await api.post('/api/v1/orders/', orderData);
    console.log('Respuesta de la API:', response);
    console.log('Datos de la orden creada:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error al crear la orden:', error.response?.data || error.message);
    throw new Error('Error al crear la orden');
  }
};

// Obtener detalles de un embarque
export const getShipmentDetails = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener detalles del embarque:', error.response?.data || error.message);
    throw new Error('Error al obtener detalles del embarque');
  }
};

// Obtener órdenes disponibles para vincular a un embarque
export const getAvailableOrders = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/orders/available?shipment_id=${shipmentId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener órdenes disponibles:', error.response?.data || error.message);
    throw new Error('Error al obtener órdenes disponibles');
  }
};

// Vincular órdenes a un embarque
export const linkOrdersToShipment = async (shipmentId, orderIds) => {
  try {
    const response = await api.post(`/api/v1/shipments/${shipmentId}/link-orders`, { orderIds });
    // El registro de costos se crea automáticamente en el backend
    return response.data;
  } catch (error) {
    console.error('Error al vincular órdenes:', error);
    throw error;
  }
};

export const updateShipmentStatus = async (shipmentId, newStatus, newSubstatus) => {
  try {
    const response = await api.patch(`/api/v1/shipments/${shipmentId}`, { status: newStatus, Substatus: newSubstatus });
    return response.data;
  } catch (error) {
    console.error('Error updating shipment status:', error);
    throw error;
  }
};

export const getClients = async () => {
  const response = await fetch('/api/v1/clients/');
  if (!response.ok) {
    throw new Error('Error fetching clients');
  }
  return response.json();
};


export const cambiarEstatusOrden = async (orderId, datos) => {
  console.log('Datos de la orden:', datos);
  try {
    const response = await api.post(`/api/v1/orders/${orderId}/change-status`, datos);
    
    return response.data; // Retorna la respuesta del servidor
    
  } catch (error) {
    console.error('Error al cambiar el estatus de la orden:', error);
    throw error; // Lanza el error para manejarlo en el componente
  }
  
};

export const cambiarEstatusEmbarque = async (shipmentId, { status, Substatus }) => {
  try {
    
    const response = await api.put(
      `/api/v1/shipments/${shipmentId}/update-status`, // Endpoint
      { status, Substatus }, // Datos del cuerpo
      {
        headers: {
          'Content-Type': 'application/json', // Tipo de contenido
        },
      }
    );
    console.log('Respuesta de la API al actualizar el embarque:', response.data);
    // Retorna los datos de la respuesta
    return response.data;
    
  } catch (error) {
    // Manejo de errores
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      throw new Error(error.response.data.message || 'Error al actualizar el estado del embarque');
    } else if (error.request) {
      // La solicitud fue hecha pero no se recibió respuesta
      throw new Error('No se recibió respuesta del servidor');
    } else {
      // Algo salió mal al configurar la solicitud
      throw new Error('Error al configurar la solicitud');
    }
  }
};

export const updateOrdersStatusByShipment = async (shipmentId, newStatus, newSubstatus, paradaActual=null, orderIds=null) => {
  try {
    const requestData = {
      status: newStatus,
      Substatus: newSubstatus,
      parada_actual: paradaActual, // Opcional: solo actualiza órdenes de esta parada
    };

    // Si se proporciona una lista específica de IDs de órdenes, incluirla en la solicitud
    if (orderIds && orderIds.length > 0) {
      requestData.order_ids = orderIds;
    }

    const response = await api.patch(`/api/v1/shipments/${shipmentId}/update-orders-status`, requestData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar las órdenes:', error);
    throw error;
  }
};

// Función para actualizar una orden
export const updateOrder = async (orderId, orderData) => {
  try {
    // Handle special case for CLIENTE ESPECIAL
    let dataToSend = orderData;
    
    // If this is a CLIENTE ESPECIAL order with custom address fields
    if (orderData.customer === "CLIENTE ESPECIAL" && 
        orderData.address && 
        orderData.city && 
        orderData.state) {
      
      dataToSend = {
        ...orderData,
        // Include any special flags or processing needed for CLIENTE ESPECIAL
        isCustomAddress: true
      };
    }
    
    const response = await api.put(`/api/v1/orders/edit/${orderId}`, dataToSend);
    console.log('Orden actualizada:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar la orden:', error.response?.data || error.message);
    throw new Error('Error al actualizar la orden');
  }
};

export const actualizarReentregaOrden = async (orderId, datos) => {
  try {
    const response = await api.patch(`/api/v1/orders/${orderId}/reentrega`, datos);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar la reentrega de la orden:', error);
    throw error;
  }
};

export const createCostRecord = async (costData) => {
  try {
    const response = await api.post('/api/v1/cost-records/', costData);
    return response.data;
  } catch (error) {
    console.error('Error al crear registro de costo:', error);
    throw error;
  }
};

export const getCostRecords = async (params) => {
  try {
    const response = await api.get('/api/v1/cost-records/', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener registros de costo:', error);
    throw error;
  }
};

export const updateCostRecord = async (recordId, costData) => {
  try {
    const response = await api.put(`/api/v1/cost-records/${recordId}`, costData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar registro de costo:', error);
    throw error;
  }
};

export const registerShipmentCosts = async (shipmentId, costData) => {
  try {
    const response = await api.post(`/api/v1/shipments/${shipmentId}/costs`, costData);
    return response.data;
  } catch (error) {
    console.error('Error al registrar costos del embarque:', error);
    throw error;
  }
};

export const getShipmentCosts = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/costs`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener costos del embarque:', error);
    throw error;
  }
};

// Add a new function to update an existing shipment cost
export const updateShipmentCost = async (costId, costData) => {
  try {
    const response = await api.patch(`/api/v1/costs/${costId}`, costData);
    return response.data;
  } catch (error) {
    console.error('Error updating cost record:', error);
    throw error;
  }
};

// Add a new function to delete a cost record
export const deleteShipmentCost = async (costId) => {
  try {
    const response = await api.delete(`/api/v1/costs/${costId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting cost record:', error);
    throw error;
  }
};

// Función para eliminar un usuario
export const deleteUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No hay token de autenticación');
    }
    
    const response = await api.delete(`/api/v1/auth/users/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar usuario:', error.response?.data || error.message);
    throw new Error('Error al eliminar el usuario');
  }
};

export const createIncident = async (incidentData) => {
  try {
    const response = await api.post('/api/v1/incidents/', incidentData);
    return response.data;
  } catch (error) {
    console.error('Error al crear la incidencia:', error.response?.data || error.message);
    throw new Error('Error al crear la incidencia');
  }
};

// Anticipos API functions
export const getAnticipos = async (params = {}) => {
  try {
    const response = await api.get('/api/v1/anticipos/', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener anticipos:', error);
    throw error;
  }
};

export const getAnticipo = async (anticipoId) => {
  try {
    const response = await api.get(`/api/v1/anticipos/${anticipoId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener anticipo:', error);
    throw error;
  }
};

export const createAnticipo = async (anticipoData) => {
  try {
    const response = await api.post('/api/v1/anticipos/', anticipoData);
    return response.data;
  } catch (error) {
    console.error('Error al crear anticipo:', error);
    throw error;
  }
};

// Function to update an anticipo
export const updateAnticipo = async (id, anticipoData) => {
  if (!id || isNaN(id)) {
    throw new Error('Invalid anticipo ID');
  }
  
  const token = localStorage.getItem('token');
  try {
    const BACKEND_URL = process.env.REACT_APP_API_URL;  
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/v1/anticipos/${id}`,
      anticipoData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating anticipo:', error);
    throw error;
  }
};

export const deleteAnticipo = async (anticipoId) => {
  try {
    const response = await api.delete(`/api/v1/anticipos/${anticipoId}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar anticipo:', error);
    throw error;
  }
};

export const getShipmentAnticipos = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/anticipos`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener anticipos del embarque:', error);
    throw error;
  }
};

// Vale de gasolina API functions
export const getValesGasolina = async (params = {}) => {
  try {
    // Eliminar la limitación de registros para obtener todos los vales de gasolina
    params.limit = 1000000; // Usar un número muy grande para asegurar que se obtengan todos los registros
    const response = await api.get('/api/v1/vales-gasolina', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching vales de gasolina:', error);
    throw error;
  }
};

export const getValeGasolina = async (valeId) => {
  try {
    const response = await api.get(`/api/v1/vales-gasolina/${valeId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vale de gasolina:', error);
    throw error;
  }
};

export const createValeGasolina = async (valeData) => {
  try {
    const response = await api.post('/api/v1/vales-gasolina/', valeData);
    return response.data;
  } catch (error) {
    console.error('Error creating vale de gasolina:', error);
    if (error.response) {
      // The server responded with an error status
      console.error('Server response error:', error.response.data);
      throw new Error(`Error del servidor: ${error.response.data.detail || error.response.statusText}`);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      throw new Error('No se recibió respuesta del servidor. Verifique su conexión a internet.');
    } else {
      // Something happened in setting up the request
      console.error('Request setup error:', error.message);
      throw error;
    }
  }
};

export const updateValeGasolina = async (valeId, valeData) => {
  try {
    const response = await api.put(`/api/v1/vales-gasolina/${valeId}`, valeData);
    return response.data;
  } catch (error) {
    console.error('Error updating vale de gasolina:', error);
    if (error.response) {
      throw new Error(`Error del servidor: ${error.response.data.detail || error.response.statusText}`);
    } else if (error.request) {
      throw new Error('No se recibió respuesta del servidor. Verifique su conexión a internet.');
    } else {
      throw error;
    }
  }
};

export const deleteValeGasolina = async (valeId) => {
  try {
    const response = await api.delete(`/api/v1/vales-gasolina/${valeId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting vale de gasolina:', error);
    throw error;
  }
};

// Add this function to check if license plates exist
export const checkPlacasExist = async (placas) => {
  try {
    const response = await api.get(`/api/v1/vehicles/check-placas/${placas}`);
    return response.data;
  } catch (error) {
    console.error('Error checking license plates:', error);
    throw error;
  }
};

// Add this function to check if remolque license plates exist
export const checkRemolquePlacasExist = async (placas) => {
  try {
    const response = await api.get(`/api/v1/remolques/check-placas/${placas}`);
    return response.data;
  } catch (error) {
    console.error('Error checking remolque license plates:', error);
    throw error;
  }
};

// Función para enviar notificación al transportista con carta de instrucciones
export const notifyTransporter = async (shipmentId, formData) => {
  try {
    const response = await api.post(`/api/v1/shipments/${shipmentId}/notify-transporter`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al enviar notificación al transportista:', error);
    throw error;
  }
};

// Función para enviar notificación SMS al transportista
export const notifyTransporterSMS = async (shipmentId, smsData) => {
  try {
    const response = await api.post(`/api/v1/shipments/${shipmentId}/notify-transporter-sms`, smsData);
    return response.data;
  } catch (error) {
    console.error('Error al enviar SMS al transportista:', error);
    throw error;
  }
};

// Transport line API functions
export const updateTransportLine = async (lineId, lineData) => {
  try {
    const response = await api.patch(`/api/v1/lineas_transporte/${lineId}`, lineData);
    return response.data;
  } catch (error) {
    console.error('Error updating transport line:', error);
    throw error;
  }
};

// Add new function to get validation data for all transport lines in a single request
export const getTransportLinesValidationData = async () => {
  try {
    const response = await api.get('/api/v1/lineas_transporte/validation-data');
    return response.data;
  } catch (error) {
    console.error('Error fetching transport lines validation data:', error);
    return {}; // Return empty object on error
  }
};

// Add this new function for creating transport lines
export const createTransportLine = async (lineData) => {
  try {
    const response = await api.post('/api/v1/lineas_transporte/', lineData);
    return response.data;
  } catch (error) {
    console.error('Error creating transport line:', error);
    throw error;
  }
};

export const blockTransportLine = async (lineId, blockReason) => {
  try {
    const response = await api.post(`/api/v1/lineas_transporte/${lineId}/block`, {
      block_reason: blockReason
    });
    return response.data;
  } catch (error) {
    console.error('Error blocking transport line:', error);
    throw error;
  }
};

export const unblockTransportLine = async (lineId) => {
  try {
    const response = await api.post(`/api/v1/lineas_transporte/${lineId}/unblock`, {});
    return response.data;
  } catch (error) {
    console.error('Error unblocking transport line:', error);
    throw error;
  }
};

export const updateTransportLineStatus = async (lineId, active) => {
  try {
    const response = await api.patch(`/api/v1/lineas_transporte/${lineId}/status`, {
      active
    });
    return response.data;
  } catch (error) {
    console.error('Error updating transport line status:', error);
    throw error;
  }
};

// Driver API functions
export const getDriversByTransportLine = async (lineaTransporteId) => {
  try {
    const response = await api.get(`/api/v1/operadores/${lineaTransporteId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching drivers:', error);
    throw error;
  }
};

export const createDriver = async (driverData) => {
  try {
    const response = await api.post('/api/v1/operadores/', driverData);
    return response.data;
  } catch (error) {
    console.error('Error creating driver:', error);
    throw error;
  }
};

export const updateDriver = async (driverId, driverData) => {
  try {
    const response = await api.put(`/api/v1/operadores/${driverId}`, driverData);
    return response.data;
  } catch (error) {
    console.error('Error updating driver:', error);
    throw error;
  }
};

export const deleteDriver = async (driverId) => {
  try {
    const response = await api.delete(`/api/v1/operadores/${driverId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting driver:', error);
    throw error;
  }
};

// Document API functions
export const getDocumentsByTransportLine = async (lineaTransporteId) => {
  try {
    const response = await api.get(`/api/v1/documents/${lineaTransporteId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

export const uploadDocument = async (formData) => {
  try {
    const response = await api.post('/api/v1/upload-to-drive', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

export const deleteDocument = async (documentId) => {
  try {
    const response = await api.delete(`/api/v1/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

// Trailer (Remolque) API functions
// Removed duplicate declaration of getTrailersByTransportLine

export const createTrailer = async (trailerData) => {
  try {
    const response = await api.post('/api/v1/remolques/', trailerData);
    return response.data;
  } catch (error) {
    console.error('Error creating trailer:', error);
    throw error;
  }
};

export const updateTrailer = async (trailerId, trailerData) => {
  try {
    const response = await api.put(`/api/v1/remolques/${trailerId}`, trailerData);
    return response.data;
  } catch (error) {
    console.error('Error updating trailer:', error);
    throw error;
  }
};

export const deleteTrailer = async (trailerId) => {
  try {
    const response = await api.delete(`/api/v1/remolques/${trailerId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting trailer:', error);
    throw error;
  }
};

// Vehicle API functions
export const getVehiclesByTransportLine = async (lineaTransporteId) => {
  try {
    const response = await api.get(`/api/v1/vehicles/${lineaTransporteId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
};

export const createVehicle = async (vehicleData) => {
  try {
    const response = await api.post('/api/v1/vehicles/', vehicleData);
    return response.data;
  } catch (error) {
    console.error('Error creating vehicle:', error);
    throw error;
  }
};

export const updateVehicle = async (vehicleId, vehicleData) => {
  try {
    const response = await api.put(`/api/v1/vehicles/${vehicleId}`, vehicleData);
    return response.data;
  } catch (error) {
    console.error('Error updating vehicle:', error);
    throw error;
  }
};

export const deleteVehicle = async (vehicleId) => {
  try {
    const response = await api.delete(`/api/v1/vehicles/${vehicleId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting vehicle:', error);
    throw error;
  }
};

// Settings page API functions
export const getUserProfile = async () => {
  try {
    const response = await api.get('/api/v1/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error.response?.data || error.message);
    throw new Error('Error al obtener la información del usuario');
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/api/v1/auth/update-profile', profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error.response?.data || error.message);
    throw new Error('Error al actualizar el perfil de usuario');
  }
};

export const changeUserPassword = async (passwordData) => {
  try {
    const response = await api.post('/api/v1/auth/change-password', passwordData);
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error.response?.data || error.message);
    throw new Error('Error al cambiar la contraseña');
  }
};

export const getAllUsers = async () => {
  try {
    const response = await api.get('/api/v1/auth/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users list:', error.response?.data || error.message);
    throw new Error('Error al obtener la lista de usuarios');
  }
};

export const updateUserRole = async (userId, newRole) => {
  try {
    const response = await api.put(`/api/v1/auth/users/${userId}/role`, { new_role: newRole });
    return response.data;
  } catch (error) {
    console.error('Error updating user role:', error.response?.data || error.message);
    throw new Error('Error al actualizar el rol del usuario');
  }
};

// Role management API functions
export const getAllRoles = async () => {
  try {
    const response = await api.get('/api/v1/roles/');
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error.response?.data || error.message);
    throw new Error('Error al obtener la lista de roles');
  }
};

export const createRole = async (roleName, permissions) => {
  try {
    const response = await api.post('/api/v1/roles/', { 
      name: roleName, 
      permissions 
    });
    return response.data;
  } catch (error) {
    console.error('Error creating role:', error.response?.data || error.message);
    throw new Error('Error al crear el rol');
  }
};

export const updateRolePermissions = async (roleId, permissions) => {
  try {
    const response = await api.put(`/api/v1/roles/${roleId}/permissions`, { 
      permissions 
    });
    return response.data;
  } catch (error) {
    console.error('Error updating role permissions:', error.response?.data || error.message);
    throw new Error('Error al actualizar los permisos del rol');
  }
};

export const deleteRole = async (roleId) => {
  try {
    const response = await api.delete(`/api/v1/roles/${roleId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting role:', error.response?.data || error.message);
    throw new Error('Error al eliminar el rol');
  }
};

// New user registration function
export const createUser = async (userData) => {
  try {
    const response = await api.post('/api/v1/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error.response?.data || error.message);
    throw new Error('Error al crear usuario');
  }
};

// Order-related API functions
export const getWarehouses = async () => {
  const response = await api.get('/api/v1/bodegas');
  return response.data;
};

export const getProducts = async () => {
  try {
    const response = await api.get('/api/v1/productos');
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error.response?.data || error.message);
    throw new Error('Error al obtener los productos');
  }
};

export const getAccounts = async () => {
  try {
    const response = await api.get('/api/v1/cuentas');
    return response.data;
  } catch (error) {
    console.error('Error fetching accounts:', error.response?.data || error.message);
    throw new Error('Error al obtener las cuentas');
  }
};

export const getClientLocations = async () => {
  try {
    const response = await api.get('/api/v1/clients/');
    return response.data;
  } catch (error) {
    console.error('Error fetching client locations:', error.response?.data || error.message);
    throw new Error('Error al obtener las ubicaciones de clientes');
  }
};

export const deleteOrder = async (orderId) => {
  try {
    const response = await api.delete(`/api/v1/orders/delete/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting order:', error.response?.data || error.message);
    throw new Error('Error al eliminar la orden');
  }
};

export const checkOrderNumberExists = async (orderNumber) => {
  try {
    const response = await api.get(`/api/v1/orders/check-number/${orderNumber}`);
    return response.data;
  } catch (error) {
    console.error('Error checking order number:', error.response?.data || error.message);
    throw new Error('Error al verificar el número de orden');
  }
};

// Update shipment status
export const updateShipmentStatusOnly = async (shipmentId, statusData) => {
  const response = await api.patch(`/api/v1/shipments/${shipmentId}`, statusData);
  return response.data;
};

// Update warehouse status for orders
export const updateOrdersWarehouseStatus = async (orderIds, bodegaDev, devStatus) => {
  const response = await api.patch('/api/v1/orders/update-warehouse-status', {
    order_ids: orderIds,
    bodega_dev: bodegaDev,
    dev_status: devStatus
  });
  return response.data;
};

// Update dev route datetime in metrics
export const updateDevRouteDt = async (orderIds) => {
  const response = await api.patch('/api/v1/metrics/update-dev-route-dt', {
    order_ids: orderIds
  });
  return response.data;
};

// Update devolucion datetime in metrics
export const updateDevolucionDt = async (orderIds) => {
  const response = await api.patch('/api/v1/metrics/update-devolucion-dt', {
    order_ids: orderIds
  });
  return response.data;
};

// Update appointment for redelivery
export const updateAppointment = async (orderIds, appointmentDateTime) => {
  const response = await api.patch('/api/v1/orders/update-appointment', {
    order_ids: orderIds,
    appointment_try: appointmentDateTime
  });
  return response.data;
};

// Update reentrega arrival time
export const updateReentregaAta = async (orderIds) => {
  const response = await api.patch('/api/v1/update-reentrega-ata', {
    order_ids: orderIds
  });
  return response.data;
};

// Update reentrega ramp status
export const updateReentregaRamp = async (orderIds) => {
  const response = await api.patch('/api/v1/update-reentrega-ramp', {
    order_ids: orderIds
  });
  return response.data;
};

// Update order status for a specific shipment
export const updateOrderStatus = async (shipmentId, statusData) => {
  const response = await api.patch(`/api/v1/shipments/${shipmentId}/update-orders-status`, statusData);
  return response.data;
};

// Get shipment by reference
export const getShipmentByReference = async (reference) => {
  try {
    const response = await api.get(`/api/v1/shipments?reference=${reference}`);
    // Verificar si la respuesta tiene datos
    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
      return response.data[0]; // Devolver el primer elemento si existe
    } else {
      // Si la respuesta está vacía o no es un array, devolver null
      console.warn('No se encontró el embarque con referencia:', reference);
      return null;
    }
  } catch (error) {
    // Manejar el error 404 específicamente
    if (error.response && error.response.status === 404) {
      console.warn('No se encontró el embarque con referencia:', reference);
      return null;
    }
    // Relanzar cualquier otro error
    console.error('Error al buscar embarque por referencia:', error);
    throw error;
  }
};

// Obtener solo embarques activos (status != FINALIZADO y órdenes sin dev_status=DEVUELTO)
export const getActiveShipments = async () => {
  try {
    // Endpoint optimizado, asumiendo que el backend soporta estos filtros
    const response = await api.get('/api/v1/shipments?status_ne=FINALIZADO&dev_status_ne=DEVUELTO');
    return response.data;
  } catch (error) {
    console.error('Error fetching active shipments:', error);
    throw error;
  }
};

// Get vehicles for a specific transport line
export const getVehiclesByLineId = async (lineId) => {
  try {
    const response = await api.get(`/api/v1/lineas_transporte/${lineId}/vehiculos`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicles for transport line:', error);
    return []; // Return empty array on error to prevent failures
  }
};

// Update multiple orders' stops for a shipment
export const updateOrdersStops = async (orderUpdates) => {
  try {
    const updatePromises = orderUpdates.map(({ orderId, stop }) => 
      api.patch(`/api/v1/orders/${orderId}/update-stop`, { stop })
    );
    
    const results = await Promise.all(updatePromises);
    return results.map(result => result.data);
  } catch (error) {
    console.error('Error updating order stops:', error);
    throw error;
  }
};

// Unlinking orders from a shipment
export const unlinkOrdersFromShipment = async (shipmentId, orderIds) => {
  try {
    const response = await api.post(`/api/v1/shipments/${shipmentId}/unlink-orders`, { orderIds });
    return response.data;
  } catch (error) {
    console.error('Error unlinking orders from shipment:', error);
    throw error;
  }
};

// Get requirements for shipments
export const getRequirements = async () => {
  try {
    const response = await api.get('/api/v1/requerimientos');
    return response.data;
  } catch (error) {
    console.error('Error fetching requirements:', error);
    throw error;
  }
};

// Get trailers (remolques) for a transport line
export const getTrailersByTransportLine = async (lineId) => {
  try {
    const response = await api.get(`/api/v1/lineas-transporte/${lineId}/remolques`);
    return response.data;
  } catch (error) {
    console.error('Error fetching trailers for transport line:', error);
    return []; // Return empty array on error to prevent failures
  }
};

// Update order stop number
export const updateOrderStop = async (orderId, stopNumber) => {
  try {
    const response = await api.patch(`/api/v1/orders/${orderId}/update-stop`, { 
      stop: stopNumber 
    });
    return response.data;
  } catch (error) {
    console.error('Error updating order stop:', error);
    throw error;
  }
};

// Update shipment loading time
export const updateShipmentLoadingTime = async (shipmentId, pickupDate) => {
  try {
    const response = await api.patch(`/api/v1/shipments/${shipmentId}/update-loading-time`, { 
      pickup_date: pickupDate 
    });
    return response.data;
  } catch (error) {
    console.error('Error updating shipment loading time:', error);
    throw error;
  }
};

// Update shipment transport details (combined function)
export const updateShipmentTransportDetails = async (shipmentId, transportDetails) => {
  try {
    const response = await api.patch(`/api/v1/shipments/${shipmentId}/update-transport-details`, transportDetails);
    return response.data;
  } catch (error) {
    console.error('Error updating shipment transport details:', error);
    throw error;
  }
};

// Evidence API functions
export const uploadEvidence = async (shipmentId, file, onProgress) => {
  const maxRetries = 3;
  const baseTimeout = 30000; // 30 seconds base timeout
  let attempt = 0;
  
  // Comprimir el PDF si es necesario (archivos grandes)
  let fileToUpload = file;
  if (file.type === 'application/pdf' && needsCompression(file)) {
    try {
      console.log(`Archivo PDF grande (${(file.size / (1024 * 1024)).toFixed(2)} MB). Iniciando compresión...`);
      fileToUpload = await compressPdf(file);
      console.log(`PDF comprimido con éxito. Tamaño nuevo: ${(fileToUpload.size / (1024 * 1024)).toFixed(2)} MB`);
    } catch (compressionError) {
      console.error('Error durante la compresión del PDF:', compressionError);
      fileToUpload = file; // Usar el archivo original si falla la compresión
    }
  }
  
  while (attempt < maxRetries) {
    try {
      // Incrementalmente aumentar el timeout en cada reintento
      const timeout = baseTimeout * (attempt + 1);
      
      const formData = new FormData();
      formData.append('file', fileToUpload);
      
      console.log(`Intento ${attempt + 1}/${maxRetries} para subir evidencia. Timeout: ${timeout}ms`);
      
      const controller = new AbortController(); // Controlador para cancelar la solicitud
      const timeoutId = setTimeout(() => controller.abort(), timeout); // Configurar el tiempo límite
      
      const response = await api.post(
        `/api/v1/shipments/${shipmentId}/upload-evidence`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: timeout,
          signal: controller.signal, // Usar AbortController para mejor manejo de timeouts
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Progreso de subida: ${percentCompleted}%`);
            // Si se proporciona una función de callback para el progreso, la llamamos
            if (onProgress && typeof onProgress === 'function') {
              onProgress(percentCompleted);
            }
          },
        }
      );
      
      clearTimeout(timeoutId); // Limpiar el timeout
      return response.data;
    } catch (error) {
      attempt++;
      
      // Solo registramos el error si es el último intento o si no es un error de timeout/red
      if (attempt >= maxRetries || 
          (error.code !== 'ECONNABORTED' && !error.message.includes('timeout') && 
           error.response?.status !== 502 && error.response?.status !== 503 && error.response?.status !== 504)) {
        console.error(`Error uploading evidence (attempt ${attempt}/${maxRetries}):`, error);
        throw error;
      }
      
      console.warn(`Reintento ${attempt}/${maxRetries} debido a: ${error.message || 'Error de red/timeout'}`);
      
      // Esperar un tiempo antes de reintentar (backoff exponencial)
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt-1))); // Usamos backoff exponencial real
    }
  }
};

export const uploadPhotos = async (shipmentId, files) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post(
      `/api/v1/shipments/${shipmentId}/upload-photos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading photos:', error);
    throw error;
  }
};

export const updatePodTime = async (shipmentReference, timestamp) => {
  try {
    const response = await api.put(
      `/api/v1/metrics/${shipmentReference}/update-podtime`,
      {
        order_Podtime: timestamp,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating POD time:', error);
    throw error;
  }
};

export const deleteEvidence = async (shipmentId, evidenceId) => {
  try {
    const response = await api.delete(
      `/api/v1/shipments/${shipmentId}/delete-evidence`,
      {
        data: { evidence_id: evidenceId },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting evidence:', error);
    throw error;
  }
};

export const deletePhoto = async (shipmentId, photoId) => {
  try {
    const response = await api.delete(
      `/api/v1/shipments/${shipmentId}/delete-photo/${photoId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

export const deleteAllPhotos = async (shipmentId) => {
  try {
    const response = await api.delete(
      `/api/v1/shipments/${shipmentId}/delete-photos`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting all photos:', error);
    throw error;
  }
};

// Transport Line Metrics
export const getTransportLineMetrics = async () => {
  try {
    const response = await api.get('/api/v1/lineas_transporte/metrics');
    return response.data;
  } catch (error) {
    console.error('Error fetching transport line metrics:', error);
    throw error;
  }
};

// Pending Transport Lines
export const getPendingTransportLines = async () => {
  try {
    const response = await api.get('/api/v1/lineas_transporte/pending');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending transport lines:', error);
    throw error;
  }
};

// Authorize Transport Line
export const authorizeTransportLine = async (lineId, authorizationStatus) => {
  try {
    const response = await api.post(`/api/v1/lineas_transporte/${lineId}/authorize`, {
      authorization_status: authorizationStatus
    });
    return response.data;
  } catch (error) {
    console.error('Error authorizing transport line:', error);
    throw error;
  }
};

// Logs API functions
export const getLogs = async (params = {}) => {
  try {
    // Using the correct API path - without the /api/v1 prefix since it's not used in the backend
    const response = await api.get('/api/v1/logs', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching logs:', error);
    throw error;
  }
};

// Get inbound shipment by reference
export const getInboundShipmentByReference = async (reference) => {
  try {
    // Use the specific endpoint for getting inbound shipment by reference
    const response = await api.get(`/api/v1/inbound/shipments-by-reference/${reference}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inbound shipment by reference:', error);
    if (error.response && error.response.status === 404) {
      return null; // Return null if shipment not found
    }
    throw error;
  }
};

// Evidence API functions for inbound shipments
export const uploadInboundEvidence = async (shipmentId, file, onProgress) => {
  const maxRetries = 3;
  const baseTimeout = 30000; // 30 seconds base timeout
  let attempt = 0;
  
  // Comprimir el PDF si es necesario (archivos grandes)
  let fileToUpload = file;
  if (file.type === 'application/pdf' && needsCompression(file)) {
    try {
      console.log(`Archivo PDF grande (${(file.size / (1024 * 1024)).toFixed(2)} MB). Iniciando compresión...`);
      fileToUpload = await compressPdf(file);
      console.log(`PDF comprimido con éxito. Tamaño nuevo: ${(fileToUpload.size / (1024 * 1024)).toFixed(2)} MB`);
    } catch (compressionError) {
      console.error('Error durante la compresión del PDF:', compressionError);
      fileToUpload = file; // Usar el archivo original si falla la compresión
    }
  }
  
  while (attempt < maxRetries) {
    try {
      // Incrementalmente aumentar el timeout en cada reintento
      const timeout = baseTimeout * (attempt + 1);
      
      const formData = new FormData();
      formData.append('file', fileToUpload);
      
      console.log(`Intento ${attempt + 1}/${maxRetries} para subir evidencia inbound. Timeout: ${timeout}ms`);
      
      const controller = new AbortController(); // Controlador para cancelar la solicitud
      const timeoutId = setTimeout(() => controller.abort(), timeout); // Configurar el tiempo límite
      
      const response = await api.post(
        `/api/v1/inbound/shipments/${shipmentId}/upload-evidence`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: timeout,
          signal: controller.signal, // Usar AbortController para mejor manejo de timeouts
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Progreso de subida: ${percentCompleted}%`);
            // Si se proporciona una función de callback para el progreso, la llamamos
            if (onProgress && typeof onProgress === 'function') {
              onProgress(percentCompleted);
            }
          },
        }
      );
      
      clearTimeout(timeoutId); // Limpiar el timeout
      return response.data;
    } catch (error) {
      attempt++;
      
      // Solo registramos el error si es el último intento o si no es un error de timeout/red
      if (attempt >= maxRetries || 
          (error.code !== 'ECONNABORTED' && !error.message.includes('timeout') && 
           error.response?.status !== 502 && error.response?.status !== 503 && error.response?.status !== 504)) {
        console.error(`Error uploading inbound evidence (attempt ${attempt}/${maxRetries}):`, error);
        throw error;
      }
      
      console.warn(`Reintento ${attempt}/${maxRetries} debido a: ${error.message || 'Error de red/timeout'}`);
      
      // Esperar un tiempo antes de reintentar (backoff exponencial)
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt-1))); // Usamos backoff exponencial real
    }
  }
};

export const uploadInboundPhotos = async (shipmentId, files) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post(
      `/api/v1/inbound/shipments/${shipmentId}/upload-photos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading inbound photos:', error);
    throw error;
  }
};

export const deleteInboundEvidence = async (shipmentId) => {
  try {
    const response = await api.delete(
      `/api/v1/inbound/shipments/${shipmentId}/delete-evidence`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting inbound evidence:', error);
    throw error;
  }
};

export const deleteInboundPhoto = async (shipmentId, photoId) => {
  try {
    const response = await api.delete(
      `/api/v1/inbound/shipments/${shipmentId}/delete-photo/${photoId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting inbound photo:', error);
    throw error;
  }
};

export const deleteAllInboundPhotos = async (shipmentId) => {
  try {
    const response = await api.delete(
      `/api/v1/inbound/shipments/${shipmentId}/delete-photos`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting all inbound photos:', error);
    throw error;
  }
};

// Rate (Tarifario) API functions
export const getRatesByTransportLine = async (lineaTransporteId) => {
  try {
    const response = await api.get(`/api/v1/lineas_transporte/${lineaTransporteId}/tarifarios`);
    return response.data;
  } catch (error) {
    console.error('Error fetching rates:', error);
    throw error;
  }
};

export const createRate = async (rateData) => {
  try {
    const response = await api.post('/api/v1/tarifarios/', rateData);
    return response.data;
  } catch (error) {
    console.error('Error creating rate:', error);
    throw error;
  }
};

export const updateRate = async (rateId, rateData) => {
  try {
    const response = await api.put(`/api/v1/tarifarios/${rateId}`, rateData);
    return response.data;
  } catch (error) {
    console.error('Error updating rate:', error);
    throw error;
  }
};

export const deleteRate = async (rateId) => {
  try {
    const response = await api.delete(`/api/v1/tarifarios/${rateId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting rate:', error);
    throw error;
  }
};

// Ciudad (Ciudades de México) API functions
export const getCities = async (query = '') => {
  try {
    // If query is too short, get popular cities instead
    if (query.length < 2) {
      return await getPopularCities();
    }
    
    const url = `/api/v1/ciudades/buscar/?q=${encodeURIComponent(query)}&limit=50`;
    console.log('Fetching cities with URL:', url);
    
    const response = await api.get(url);
    console.log('Cities API response:', response.data);
    
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching cities:', error);
    console.error('Error details:', error.response?.data || error.message);
    return []; // Devolver un array vacío en lugar de lanzar una excepción
  }
};

// Añadir una función para obtener ciudades populares
export const getPopularCities = async () => {
  try {
    // Removed limit parameter to get all cities
    const response = await api.get('/api/v1/ciudades/');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching popular cities:', error);
    return [];
  }
};

// Add a function to get all cities (use carefully due to potential large data size)
export const getAllCities = async () => {
  try {
    // Using the /all/ endpoint without a limit parameter
    const response = await api.get('/api/v1/ciudades/all/');
    console.log(`Fetched ${response.data.length} total cities`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching all cities:', error);
    return [];
  }
};

export const getCitiesByState = async (estado) => {
  try {
    const response = await api.get(`/api/v1/ciudades/estado/${encodeURIComponent(estado)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching cities by state:', error);
    throw error;
  }
};

export const getStates = async () => {
  try {
    const response = await api.get('/api/v1/estados/');
    return response.data;
  } catch (error) {
    console.error('Error fetching states:', error);
    throw error;
  }
};

export default api;

// Funciones para Auditoría de Calidad
export const uploadAuditoriaImage = async (auditoriaId, file, tipoEvidencia) => {
  try {
    // Crear un FormData para enviar el archivo
    const formData = new FormData();
    formData.append('files', file); // Cambiado a 'files' que es lo que espera el endpoint
    
    let endpoint = `/api/v1/auditorias/calidad/${auditoriaId}/upload_evidence`;
    
    // Agregar el tipo de evidencia como query parameter si está presente
    if (tipoEvidencia) {
      endpoint += `?tipo_evidencia=${tipoEvidencia}`;
    }    
    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    // La respuesta incluirá la información de las imágenes subidas, incluyendo los IDs
    // En caso de éxito, transformamos la respuesta para que sea más fácil de manejar en el cliente
    if (response.data && response.data.files) {
      // Extraer solo los IDs de las imágenes si es necesario
      const fileIds = response.data.files.map(file => file.id);
      return {
        ...response.data,
        fileIds: fileIds // Para facilitar el acceso a los IDs
      };
    }
    
    return response.data; // Retorna la respuesta completa si no pudimos procesarla
  } catch (error) {
    console.error('Error al subir imagen de auditoría:', error);
    throw error;
  }
};

export const createAuditoriaCalidad = async (auditoriaData) => {
  try {
    const response = await api.post('/api/v1/auditorias/calidad', auditoriaData);
    return response.data;
  } catch (error) {
    console.error('Error al crear auditoría de calidad:', error);
    throw error;
  }
};

export const getAuditoriasByShipmentId = async (shipmentId) => {
  try {
    // Intentamos obtener las auditorías usando el endpoint específico
    const response = await api.get(`/api/v1/auditorias/calidad/shipment/${shipmentId}`);
    return response.data;
  } catch (error) {
    // Si ocurre un error, revisamos si es porque el endpoint no existe (404)
    if (error.response && error.response.status === 404) {
      console.warn('El endpoint para obtener auditorías por shipment_id no está disponible. Posiblemente aún no implementado en el backend.');
      return []; // Retornamos un array vacío para evitar errores en el frontend
    }
    
    // Para cualquier otro error, lo propagamos
    console.error('Error al obtener auditorías por shipment_id:', error);
    throw error;
  }
};

export const getAuditoriaCalidadById = async (auditoriaId) => {
  try {
    const response = await api.get(`/api/v1/auditorias/calidad/${auditoriaId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener auditoría de calidad por ID:', error);
    throw error;
  }
};

// Add these new functions for adicionales (concepts)
export const getAdicionales = async () => {
  try {
    const response = await api.get('/api/v1/adicionales/');
    return response.data;
  } catch (error) {
    console.error('Error fetching adicionales:', error);
    return [];
  }
};

export const createAdicional = async (conceptoData) => {
  try {
    const response = await api.post('/api/v1/adicionales/', conceptoData);
    return response.data;
  } catch (error) {
    console.error('Error creating adicional:', error);
    throw error;
  }
};

// Ensure current_origin is included in the payload
export const setShipmentLoadingOrder = async (shipmentId, orderData) => {
  try {
    const payload = {
      ...orderData,
      current_origin: orderData.order_groups?.[0]?.origin || null, // Set current_origin to the first origin in the order groups
    };

    const response = await api.post(`/api/v1/shipments/${shipmentId}/loading-order`, payload);
    return response.data;
  } catch (error) {
    console.error('Error setting shipment loading order:', error);
    throw error;
  }
};

// Get loading phase information for a shipment
export const getShipmentLoadingPhaseInfo = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/loading-phase`);
    return response.data;
  } catch (error) {
    console.error('Error getting shipment loading phase info:', error);
    return {
      current_phase: 0,
      total_phases: 1,
      current_origin: '',
      next_origin: null,
      completed: false
    };
  }
};

// Advance to the next loading phase (next origin)
export const advanceLoadingPhase = async (shipmentId) => {
  try {
    const response = await api.patch(`/api/v1/shipments/${shipmentId}/advance-loading-phase`);
    return response.data;
  } catch (error) {
    console.error('Error advancing shipment loading phase:', error);
    throw error;
  }
};

// Update individual order loading sequence
export const updateOrderLoadingSequence = async (shipmentId, sequenceData) => {
  try {
    // Para simplificar, asumimos que todas las órdenes son de un mismo origen
    // y solo necesitamos actualizar su orden dentro de ese origen
    const response = await api.patch(`/api/v1/shipments/${shipmentId}/update-transport-details`, {
      loading_order_sequence: sequenceData.order_sequence
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating order loading sequence:', error);
    throw error;
  }
};

// Get the configured loading and delivery sequences for a shipment
export const getShipmentSequences = async (shipmentId) => {
  try {
    const response = await api.get(`/api/v1/shipments/${shipmentId}/sequences`);
    
    // Si la respuesta está vacía o no contiene la información esperada,
    // intentaremos construir las secuencias a partir de las órdenes del embarque
    if (!response.data.loading_sequence || Object.keys(response.data.loading_sequence).length === 0 ||
        !response.data.delivery_sequence || Object.keys(response.data.delivery_sequence).length === 0) {
      
      // Obtener el embarque con sus órdenes
      const shipmentWithOrders = await getShipmentWithOrders(shipmentId);
      
      if (shipmentWithOrders && shipmentWithOrders.orders && shipmentWithOrders.orders.length > 0) {
        const loadingSequence = {};
        const deliverySequence = {};
        const ordersWithLoadingOrder = [];
        const ordersWithStop = [];
        
        // Recorrer las órdenes para extraer sus secuencias
        shipmentWithOrders.orders.forEach(order => {
          // Secuencia de carga basada en loading_order
          if (order.loading_order !== null && order.loading_order !== undefined) {
            loadingSequence[order.id] = order.loading_order;
            ordersWithLoadingOrder.push({
              id: order.id,
              order_number: order.Order_number,
              loading_order: order.loading_order
            });
          }
          
          // Secuencia de entrega basada en Stop
          if (order.Stop !== null && order.Stop !== undefined) {
            deliverySequence[order.id] = parseInt(order.Stop);
            ordersWithStop.push({
              id: order.id,
              order_number: order.Order_number,
              stop: order.Stop
            });
          }
        });
        
        // Si hay información del embarque, usar loading_sequence y delivery_sequence del embarque
        if (shipmentWithOrders.loading_sequence) {
          // Si existe loading_sequence en el embarque, combinar con lo que ya tenemos
          if (typeof shipmentWithOrders.loading_sequence === 'object' && shipmentWithOrders.loading_sequence !== null) {
            Object.assign(loadingSequence, shipmentWithOrders.loading_sequence);
          }
        }
        
        if (shipmentWithOrders.delivery_sequence) {
          // Si existe delivery_sequence en el embarque, combinar con lo que ya tenemos
          if (typeof shipmentWithOrders.delivery_sequence === 'object' && shipmentWithOrders.delivery_sequence !== null) {
            Object.assign(deliverySequence, shipmentWithOrders.delivery_sequence);
          }
        }
        
        // Devolver las secuencias construidas y la información de debug
        const constructedResponse = {
          loading_sequence: loadingSequence,
          delivery_sequence: deliverySequence,
          debug_info: {
            orders_with_loading_order: ordersWithLoadingOrder,
            orders_with_stop: ordersWithStop,
            loading_sequence_from_shipment: shipmentWithOrders.loading_sequence || {},
            delivery_sequence_from_shipment: shipmentWithOrders.delivery_sequence || {}
          }
        };
        
        console.log('DEBUG - Constructed sequence data from orders:', constructedResponse);
        return constructedResponse;
      }
    }
    
    console.log('DEBUG - Received sequence data from backend:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching shipment sequences:', error);
    // Return default empty sequences if there's an error
    return {
      loading_sequence: {},
      delivery_sequence: {},
      debug_info: {
        orders_with_loading_order: [],
        orders_with_stop: [],
        error_message: error.message
      }
    };
  }
};

// Update individual order delivery sequence
export const updateOrderDeliverySequence = async (shipmentId, sequenceData) => {
  try {
    // Utilizar el endpoint update-transport-details que sí existe
    const response = await api.patch(`/api/v1/shipments/${shipmentId}/update-transport-details`, {
      delivery_order_sequence: sequenceData.order_sequence
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating order delivery sequence:', error);
    throw error;
  }
};

// Actualizar el loading_order de una orden individual
export const updateOrderLoadingOrder = async (orderId, loadingOrder) => {
  try {
    // Utilizar la ruta correcta que existe en el backend
    const response = await api.patch(`/api/v1/orders/${orderId}/update-loading-order`, { loading_order: loadingOrder });
    return response.data;
  } catch (error) {
    console.error('Error updating order loading order:', error);
    throw error;
  }
};

// Función para obtener permisos del usuario actual
export const getUserPermissions = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No hay token para obtener permisos');
      return null;
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/permissions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error al obtener permisos de usuario:', error);
    return null;
  }
};

export const resetUserPassword = async (userId) => {
  try {
    console.log(`Intentando resetear contraseña para usuario ID: ${userId}`);
    console.log(`URL completa: ${process.env.REACT_APP_API_URL}/api/v1/auth/users/${userId}/reset-password`);
    
    const response = await api.post(`/api/v1/auth/users/${userId}/reset-password`);
    console.log('Respuesta exitosa del reset de contraseña:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error resetting user password:', error);
    if (error.response) {
      // El servidor respondió con un error
      console.error('Respuesta del servidor:', error.response.status, error.response.data);
    } else if (error.request) {
      // La petición fue realizada pero no se recibió respuesta
      console.error('No se recibió respuesta del servidor');
    } else {
      // Algo ocurrió durante la configuración de la petición
      console.error('Error en la configuración de la petición:', error.message);
    }
    throw new Error(`Error al resetear la contraseña: ${error.message}`);
  }
};

export const putShipmentOnHold = async (shipmentId, userName) => {
  return api.post(`/api/v1/shipments/${shipmentId}/hold`, { userName });
};

export const removeShipmentFromHold = async (shipmentId, userName) => {
  return api.post(`/api/v1/shipments/${shipmentId}/unhold`, { userName });
};

export const getUserClients = async (userId) => {
  const response = await api.get(`/api/v1/auth/users/${userId}/clients`);
  return response.data;
};

/**
 * Obtiene el resumen de piezas enviadas vs recibidas para el dashboard, con filtros opcionales
 * @param {Object} filtros - { cliente, fecha_carga_inicio, fecha_carga_fin, fecha_entrega_inicio, fecha_entrega_fin, linea_transporte }
 */
export const getPiezasDashboard = async (filtros = {}) => {
  try {
    const response = await api.get('/api/v1/orders/piezas-dashboard', { params: filtros });
    return response.data;
  } catch (error) {
    console.error('Error fetching piezas dashboard:', error);
    throw error;
  }
};
